import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Nav } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSidebarFlyout } from './SidebarFlyoutContext';
import useSidebarFlyoutPosition from './useSidebarFlyoutPosition';
import './areaBudgetNavPanel.css';

function isPathActive(pathname, targetPath) {
  if (!targetPath) return false;
  return pathname === targetPath || pathname.startsWith(`${targetPath}/`);
}

function isInsideFlyoutInteraction(target, anchorRef, flyoutRef) {
  return anchorRef.current?.contains(target) || flyoutRef.current?.contains(target);
}

function normalizeFlyoutTitle(value) {
  return String(value || '').trim().toLowerCase();
}

/** Drop section headers that repeat the flyout panel title (sidebar row already names the module). */
function filterFlyoutItems(items, panelTitle) {
  const panelTitleNorm = normalizeFlyoutTitle(panelTitle);
  return items.filter((item) => {
    if (!item?.isHeader) return Boolean(item?.path);
    return normalizeFlyoutTitle(item.label) !== panelTitleNorm;
  });
}

/**
 * Sidebar row with optional flyout panel to the right (AreaBudgetNavPanel pattern).
 * Only one contextual flyout may be open at a time (SidebarFlyoutContext).
 *
 * Lifecycle:
 * - Row click toggles open/closed (explicit open when closed, close when open).
 * - Route change closes via SidebarFlyoutProvider (not while staying on the same screen).
 * - Close button, Escape, and outside click dismiss the panel.
 */
export default function SidebarFlyoutNavRow({
  flyoutId,
  label,
  flyoutTitle,
  items = [],
  SubMenuItem,
  closeMobileSidebar,
  indentClass = 'ps-5',
  flyoutWidth = 320,
  alwaysFlyout = false,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const anchorRef = useRef(null);
  const flyoutRef = useRef(null);
  const { open, openFlyout, closeFlyout } = useSidebarFlyout(flyoutId);

  const accessibleItems = items.filter((item) => item?.path || item?.isHeader);
  const linkItems = items.filter((item) => item?.path && !item.isHeader);
  const panelTitle = flyoutTitle || label;
  const flyoutItems = filterFlyoutItems(accessibleItems, panelTitle);
  const isRouteActive = linkItems.some((item) => isPathActive(location.pathname, item.path));
  const useFlyout = alwaysFlyout || linkItems.length > 1;

  const { style: flyoutStyle, updatePosition } = useSidebarFlyoutPosition(
    open,
    anchorRef,
    flyoutRef,
    flyoutWidth,
  );

  useEffect(() => {
    if (open) updatePosition();
  }, [open, flyoutItems.length, updatePosition]);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (isInsideFlyoutInteraction(event.target, anchorRef, flyoutRef)) return;
      closeFlyout();
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') closeFlyout();
    };

    // Capture phase so outside clicks dismiss before other sidebar handlers run.
    document.addEventListener('pointerdown', handlePointerDown, true);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open, closeFlyout]);

  if (linkItems.length === 0) return null;

  const navigateTo = (path) => {
    closeFlyout();
    closeMobileSidebar?.();
    navigate(path);
  };

  const stopSidebarToggle = (event) => {
    event.stopPropagation();
  };

  const handleToggle = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (open) {
      closeFlyout();
    } else {
      openFlyout();
    }
  };

  if (!useFlyout) {
    return (
      <SubMenuItem to={linkItems[0].path} indentClass={indentClass}>
        {label}
      </SubMenuItem>
    );
  }

  const flyoutPanel = open ? createPortal(
    // No visible title: the sidebar row that opened this panel is still on
    // screen right beside it, marked is-open, saying the same words. The
    // panel's name lives in aria-label, so screen readers still announce it.
    // Width follows the content — flyoutWidth is now a ceiling, not a fixed
    // size, so a two-item menu is not a mostly-empty 320px box.
    <div
      ref={flyoutRef}
      className="area-budget-nav-flyout area-budget-nav-flyout--fit"
      style={{ ...flyoutStyle, maxWidth: flyoutWidth }}
      role="menu"
      aria-label={`${panelTitle} menu`}
    >
        <div className="area-budget-nav-flyout-header">
          <button
            type="button"
            className="area-budget-nav-flyout-close"
            onClick={closeFlyout}
            aria-label={`Close ${panelTitle} menu`}
          >
            <i className="bi bi-x-lg" aria-hidden="true" />
          </button>
        </div>
        <div className="area-budget-nav-panel">
          {flyoutItems.map((item) => (
            item.isHeader ? (
              <div key={`header-${item.label}`} className="area-budget-nav-flyout-section">
                {item.label}
              </div>
            ) : (
              <button
                key={item.path}
                type="button"
                className={`area-budget-nav-new ${isPathActive(location.pathname, item.path) ? 'is-active' : ''}`}
                onClick={() => navigateTo(item.path)}
              >
                {item.label}
              </button>
            )
          ))}
      </div>
    </div>,
    document.body,
  ) : null;

  return (
    <>
      <div
        ref={anchorRef}
        className="area-budget-nav-anchor"
        onClick={stopSidebarToggle}
        onMouseDown={stopSidebarToggle}
      >
        <Nav.Link
          className={`${indentClass} py-1 d-flex align-items-center sidebar-submenu-item area-budget-nav-toggle text-white-50 ${open ? 'is-open' : ''} ${isRouteActive ? 'is-active' : ''}`}
          onClick={handleToggle}
          onMouseDown={stopSidebarToggle}
          aria-expanded={open}
          aria-haspopup="menu"
        >
          <span className="flex-grow-1">{label}</span>
          <i className={`bi bi-chevron-${open ? 'down' : 'right'} sidebar-submenu-chevron`} />
        </Nav.Link>
      </div>
      {flyoutPanel}
    </>
  );
}
